import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  MapPinIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { absencesAPI, boardAPI } from '../../services/api';
import type { PlayerAbsence } from '../../types/player';
import { describeAbsence } from '../../utils/absence';
import { AttendanceStatus, EventType } from '../../types/event';
import type { BoardPost } from '../../types/board';
import type { FamilyEvent } from '../../types/family';
import { useSportDoctorWarningDays } from '../../utils/playerMedical';
import { useFamilyAgenda } from '../../components/family/useFamilyAgenda';
import { Group, LoadingRows, PageTitle, Row, SectionLabel } from '../../components/family/ui';
import EventResponsePanel from '../../components/events/EventResponsePanel';
import EventRow from '../../components/family/EventRow';
import InstallAppBanner from '../../components/ui/InstallAppBanner';
import {
  addDays,
  dayLabel,
  dayOffset,
  eventTypeName,
  firstName,
  formatFt,
  isoDate,
  mapsUrl,
  shortNames,
  timeOf,
} from '../../components/family/format';

const HORIZON_DAYS = 21;

/** Mennyire sürgős a sportorvosi: lejárt, hamarosan lejár, vagy rendben. */
const doctorState = (validity: string | null | undefined, warningDays: number) => {
  if (!validity) return 'missing' as const;
  const days = dayOffset(validity);
  if (days < 0) return 'expired' as const;
  if (days <= warningDays) return 'expiring' as const;
  return 'ok' as const;
};

/**
 * A szülő / játékos kezdőlapja: mi a következő program és jön-e rá, mi a
 * teendő (csak ha van), és mi újság. Semmi statisztika, semmi, ami csak a
 * stábot érdekli.
 */
const FamilyHome: React.FC = () => {
  usePageTitle('Kezdőlap');
  const { user } = useAuth();
  const warningDays = useSportDoctorWarningDays();
  const [range] = useState(() => {
    const today = new Date();
    return { from: isoDate(today), to: isoDate(addDays(today, HORIZON_DAYS)) };
  });
  const { agenda, setAgenda, error } = useFamilyAgenda(range.from, range.to);
  const [posts, setPosts] = useState<BoardPost[]>([]);
  const [absences, setAbsences] = useState<PlayerAbsence[]>([]);

  // A folyamatban lévő betegség/sérülés: a kezdőlap jelezze, miért "Nem jön" a gyerek
  useEffect(() => {
    absencesAPI
      .getCurrent()
      .then(setAbsences)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    boardAPI
      .getPosts({ limit: 3 })
      .then(setPosts)
      .catch(() => undefined);
  }, []);

  const players = useMemo(() => agenda?.players ?? [], [agenda]);
  const multiple = players.length > 1;
  const names = shortNames(players);

  const upcoming = useMemo(() => {
    const now = new Date();
    return (agenda?.events ?? []).filter((e) => new Date(e.endTime) > now);
  }, [agenda]);

  const next = upcoming.find((e) => !e.isCancelled);
  const later = upcoming.filter((e) => e !== next).slice(0, 5);

  const updateResponse = (eventId: number) => (playerId: number, status: AttendanceStatus) => {
    setAgenda((prev) =>
      prev && {
        ...prev,
        events: prev.events.map((e) =>
          e.id === eventId
            ? { ...e, myPlayers: e.myPlayers.map((p) => (p.playerId === playerId ? { ...p, status } : p)) }
            : e
        ),
      }
    );
  };

  // Teendők: csak ami tényleg dolgot ad
  const unanswered = upcoming.filter(
    (e) => !e.isCancelled && e.responsesEnabled && e.myPlayers.some((p) => p.inSquad && p.status === AttendanceStatus.NoResponse)
  );
  const cancelled = upcoming.filter((e) => e.isCancelled);
  const unpaid = players.reduce((sum, p) => sum + p.unpaidFeeAmount, 0);
  const doctorIssues = players
    .map((p) => ({ player: p, state: doctorState(p.sportDoctorValidity, warningDays) }))
    .filter((x) => x.state === 'expired' || x.state === 'expiring');
  const hasTodo = unanswered.length > 0 || cancelled.length > 0 || unpaid > 0 || doctorIssues.length > 0;

  const today = new Date().toLocaleDateString('hu-HU', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div>
      <PageTitle
        title={`Szia, ${firstName(user?.name ?? '')}!`}
        subtitle={today.charAt(0).toUpperCase() + today.slice(1)}
      />

      {/* Ha még nincs a telefon kezdőképernyőjén: letöltés */}
      <InstallAppBanner className="mb-4" />

      {error && !agenda && (
        <p className="rounded-2xl bg-white p-4 text-[15px] text-gray-600 ring-1 ring-black/5">
          Most nem sikerült betölteni a programot. Ellenőrizd az internetkapcsolatot, és próbáld újra.
        </p>
      )}

      {!agenda && !error && <LoadingRows rows={2} />}

      {agenda && players.length === 0 && (
        <p className="rounded-2xl bg-white p-4 text-[15px] text-gray-600 ring-1 ring-black/5">
          Még nincs játékos a fiókodhoz kötve. Szólj az edzőnek, hogy hívjon meg a gyereked adatlapjáról.
        </p>
      )}

      {agenda && players.length > 0 && (
        <>
          {next ? (
            <NextEventCard event={next} showNames={multiple} onChanged={updateResponse(next.id)} />
          ) : (
            <p className="rounded-2xl bg-white p-4 text-[15px] text-gray-600 ring-1 ring-black/5">
              A következő {HORIZON_DAYS} napban nincs program.
            </p>
          )}

          {hasTodo && (
            <>
              <SectionLabel>Teendők</SectionLabel>
              <Group>
                {unanswered.length > 0 && (
                  <Row
                    to="/program?filter=unanswered"
                    icon={QuestionMarkCircleIcon}
                    title={
                      unanswered.length === 1
                        ? 'Egy eseményre még nem jeleztél'
                        : `${unanswered.length} eseményre még nem jeleztél`
                    }
                    subtitle="Jelezd, hogy jön-e - az edző ebből tervez."
                  />
                )}
                {cancelled.map((e) => (
                  <Row
                    key={e.id}
                    to={`/events/${e.id}`}
                    icon={ExclamationTriangleIcon}
                    tone="danger"
                    title={`Elmarad: ${e.title}`}
                    subtitle={`${dayLabel(e.startTime)}, ${timeOf(e.startTime)}${e.ageGroup ? ` · ${e.ageGroup}` : ''}`}
                  />
                ))}
                {doctorIssues.map(({ player, state }) => (
                  <Row
                    key={player.playerId}
                    to={`/me/players/${player.playerId}`}
                    icon={HeartIcon}
                    tone={state === 'expired' ? 'danger' : 'default'}
                    title={
                      state === 'expired'
                        ? `${multiple || !player.isSelf ? `${names[player.playerId]} sportorvosija` : 'A sportorvosid'} lejárt`
                        : `${multiple || !player.isSelf ? `${names[player.playerId]} sportorvosija` : 'A sportorvosid'} hamarosan lejár`
                    }
                    subtitle={
                      state === 'expired'
                        ? 'Érvényes sportorvosi nélkül nem léphet pályára.'
                        : `Érvényes: ${new Date(player.sportDoctorValidity!).toLocaleDateString('hu-HU')}`
                    }
                  />
                ))}
                {unpaid > 0 && (
                  <Row to="/me/fees" icon={BanknotesIcon} title="Tagdíj" subtitle="Fizetetlen" value={formatFt(unpaid)} />
                )}
              </Group>
            </>
          )}

          {later.length > 0 && (
            <>
              <SectionLabel
                action={
                  <Link to="/program" className="text-[15px] text-primary-600">
                    Teljes program
                  </Link>
                }
              >
                Utána
              </SectionLabel>
              <Group>
                {later.map((e, i) => (
                  <React.Fragment key={e.id}>
                    {(i === 0 || dayOffset(later[i - 1].startTime) !== dayOffset(e.startTime)) && (
                      <div className="bg-gray-50 px-4 py-1.5 text-[13px] font-semibold text-gray-500">
                        {dayLabel(e.startTime)}
                      </div>
                    )}
                    <EventRow event={e} showNames={multiple} />
                  </React.Fragment>
                ))}
              </Group>
            </>
          )}

          <SectionLabel>Beteg vagy sérült?</SectionLabel>
          <Group>
            {players.map((p) => {
              const absence = absences.find((a) => a.playerId === p.playerId);
              return absence ? (
                <Row
                  key={p.playerId}
                  to={`/me/players/${p.playerId}`}
                  icon={HeartIcon}
                  title={`${players.length === 1 && p.isSelf ? 'Bejelentve' : names[p.playerId]}: ${describeAbsence(absence)}`}
                  subtitle="Az érintett edzésekre és meccsekre automatikusan „Nem jön” került."
                />
              ) : (
                <Row
                  key={p.playerId}
                  to={`/me/players/${p.playerId}?absence=1`}
                  icon={HeartIcon}
                  title={players.length === 1 && p.isSelf ? 'Hiányzás bejelentése' : `${names[p.playerId]} hiányzásának bejelentése`}
                  subtitle="Az érintett edzésekre és meccsekre automatikusan „Nem jön”."
                />
              );
            })}
          </Group>
        </>
      )}

      {posts.length > 0 && (
        <>
          <SectionLabel
            action={
              <Link to="/board" className="text-[15px] text-primary-600">
                Összes
              </Link>
            }
          >
            Hírek
          </SectionLabel>
          <Group>
            {posts.map((post) => (
              <Link key={post.id} to={`/board/${post.id}`} className="block px-4 py-3 active:bg-gray-50">
                <div className="flex items-center gap-2 text-[13px] text-gray-500">
                  {!post.isReadByMe && <span className="h-2 w-2 shrink-0 rounded-full bg-primary-600" aria-label="Új" />}
                  <span className="truncate">{post.authorName}</span>
                  <span>·</span>
                  <span className="shrink-0">{new Date(post.createdAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</span>
                </div>
                <p className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-[15px] text-gray-900">{post.content}</p>
              </Link>
            ))}
          </Group>
        </>
      )}
    </div>
  );
};

/** A következő program nagyban: mikor, hol, és rögtön jelezhető, hogy jön-e. */
const NextEventCard: React.FC<{
  event: FamilyEvent;
  showNames: boolean;
  onChanged: (playerId: number, status: AttendanceStatus) => void;
}> = ({ event, showNames, onChanged }) => {
  const map = mapsUrl(event.place, event.address);
  const isMatch = event.type === EventType.Match;
  const canRespond = event.responsesEnabled && event.myPlayers.length > 0 && new Date(event.startTime) > new Date();

  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-black/5">
      <Link to={`/events/${event.id}`} className="block">
        <p className="text-[13px] font-semibold uppercase tracking-wide text-primary-600">
          {dayLabel(event.startTime)} · {timeOf(event.startTime)}–{timeOf(event.endTime)}
        </p>
        <h2 className="mt-1 text-[22px] font-bold leading-tight text-gray-900">
          {isMatch && event.opponent ? event.opponent : event.title}
        </h2>
        <p className="mt-0.5 text-[15px] text-gray-500">
          {eventTypeName[event.type]}
          {event.ageGroup && ` · ${event.ageGroup}`}
          {isMatch && event.isHomeMatch != null && (event.isHomeMatch ? ' · hazai' : ' · idegenben')}
          {event.isFutsal && ' · futsal'}
        </p>
      </Link>

      {event.place && (
        <a
          href={map ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="mt-3 flex items-start gap-2 text-[15px] text-gray-700"
        >
          <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
          <span>
            {event.place}
            {event.address && <span className="block text-[13px] text-gray-500">{event.address}</span>}
          </span>
        </a>
      )}

      {canRespond && (
        <div className="mt-4">
          <EventResponsePanel eventId={event.id} players={event.myPlayers} showNames={showNames} onStatusChange={onChanged} />
        </div>
      )}

      <Link
        to={`/events/${event.id}`}
        className="mt-3 block text-center text-[15px] font-medium text-primary-600 active:opacity-60"
      >
        Részletek, utazás
      </Link>
    </div>
  );
};

export default FamilyHome;
